export interface TransferPoint {
  lat: number;
  lng: number;
}

/**
 * Where "inside Marrakech" ends for transfer pricing. A place outside this
 * ring costs 100 MAD more (see `transferOutsideFee`).
 *
 * Owner decision 2026-09-23: only the city itself is inside, meaning the
 * medina, Gueliz, Hivernage, the airport and the neighbourhoods inside the
 * ring road. The Palmeraie, the Ourika road, the Route de Fes (Ouahat Sidi
 * Brahim) and Tamansourt are outside. It is not the municipal boundary.
 * Dar Soukkar in Agdal was added on 2026-10-12.
 * Edit it here and nowhere else: the form and the API both read it.
 *
 * Ring of [lat, lng], first point repeated last.
 */
export const MARRAKECH_TRANSFER_ZONE: ReadonlyArray<readonly [number, number]> = [
  [31.68, -8.06],
  [31.685, -8.0],
  [31.665, -7.975],
  [31.64, -7.955],
  [31.6, -7.95],
  // Dipped south around Agdal so Dar Soukkar (31.5749, -7.9789) is inside
  // (owner, 2026-10-12: its ride is 300 MAD, not 400).
  [31.585, -7.965],
  [31.568, -7.975],
  [31.568, -7.99],
  [31.575, -8.06],
  [31.61, -8.09],
  [31.65, -8.085],
  [31.68, -8.06],
];

/** Ray casting. Points exactly on an edge may fall either side; that is fine for a fee. */
export function isInsideTransferZone(
  point: TransferPoint,
  ring: ReadonlyArray<readonly [number, number]> = MARRAKECH_TRANSFER_ZONE,
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [yi, xi] = ring[i]!;
    const [yj, xj] = ring[j]!;
    const crosses = yi > point.lat !== yj > point.lat;
    if (crosses && point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The middle of Marrakech the place search is centred on (Jemaa el-Fna). */
export const MARRAKECH_CENTRE: TransferPoint = { lat: 31.6295, lng: -7.9811 };

/** Metres between two points, on a sphere; plenty for a city. */
export function metresBetween(a: TransferPoint, b: TransferPoint): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * How far from `centre` the zone surely holds and surely ends, in metres:
 * every point closer than `inside` is in the zone, every point farther than
 * `outside` is out of it. In between depends on the direction.
 *
 * The place search only knows a suggestion's distance from the centre (Google
 * gives it for free; the pin costs a details call), so it tags a suggestion
 * "In Marrakech" or "+100 MAD" only when the distance alone settles it.
 */
export function transferZoneRadii(
  centre: TransferPoint = MARRAKECH_CENTRE,
  ring: ReadonlyArray<readonly [number, number]> = MARRAKECH_TRANSFER_ZONE,
): { inside: number; outside: number } {
  // Local flat metres around the centre: fine at this size.
  const kx = metresBetween(centre, { lat: centre.lat, lng: centre.lng + 1 });
  const ky = metresBetween(centre, { lat: centre.lat + 1, lng: centre.lng });
  const xy = ([lat, lng]: readonly [number, number]) => [(lng - centre.lng) * kx, (lat - centre.lat) * ky] as const;
  let inside = Infinity;
  let outside = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [ax, ay] = xy(ring[i]!);
    const [bx, by] = xy(ring[i + 1]!);
    outside = Math.max(outside, Math.hypot(ax, ay));
    // Distance from the centre (origin) to the edge a-b.
    const dx = bx - ax;
    const dy = by - ay;
    const t = Math.max(0, Math.min(1, -(ax * dx + ay * dy) / (dx * dx + dy * dy)));
    inside = Math.min(inside, Math.hypot(ax + t * dx, ay + t * dy));
  }
  return { inside: Math.floor(inside), outside: Math.ceil(outside) };
}

/**
 * What a place at `metres` from the centre surely is: "inside" the zone,
 * "outside" it (the fee applies), or null when only the pin can tell.
 */
export function zoneByDistance(metres: number | null | undefined): "inside" | "outside" | null {
  if (metres == null || !Number.isFinite(metres)) return null;
  const { inside, outside } = transferZoneRadii();
  if (metres < inside) return "inside";
  if (metres > outside) return "outside";
  return null;
}
